/**
 * What the signed-in user is allowed to reach, as far as the panel is concerned.
 *
 * The server is the authority — every one of these decisions is made again by
 * the RBAC middleware against the target item's owning store, and a manager who
 * forges a request still gets a 403. This mirror exists only so the panel does
 * not offer a button that is guaranteed to fail, which is a worse experience
 * than not showing it at all.
 *
 * Admins carry an empty `storeIds` by convention (see `toSessionUser`), so an
 * empty list means "everything" for an admin and "nothing yet" for a manager.
 * Getting that backwards would hand a brand-new manager the whole estate, which
 * is why it is one function with a test rather than a condition repeated in
 * three components.
 */

import type { SessionUser } from '@pbtv/shared';

export function isAdmin(user: SessionUser | null): boolean {
  return user !== null && user.role === 'admin';
}

/** The stores this user can act on, or `'all'` for an admin. */
export function reachableStoreIds(user: SessionUser | null): 'all' | readonly string[] {
  if (user === null) return [];
  if (user.role === 'admin') return 'all';
  return user.storeIds;
}

export function canReachStore(user: SessionUser | null, storeId: string): boolean {
  const reachable = reachableStoreIds(user);
  return reachable === 'all' ? true : reachable.includes(storeId);
}

/** Creating, editing and deleting stores is admin-only. */
export function canManageStores(user: SessionUser | null): boolean {
  return isAdmin(user);
}

/** Managers edit the screens of the stores they hold; admins edit any. */
export function canEditScreen(user: SessionUser | null, storeId: string): boolean {
  return canReachStore(user, storeId);
}

/**
 * A manager with no stores yet sees an empty panel that is not broken, just
 * empty, and needs to be told which it is.
 */
export function hasNoScope(user: SessionUser | null): boolean {
  return user !== null && user.role !== 'admin' && user.storeIds.length === 0;
}
